import { FC, useState } from "react";
import Button from "components/Elements/Button/Button";
import { useContent } from "contexts/contentContext/contentContext";
import { useModal } from "contexts/modalContext/modalContext";
import Image from "components/Elements/Image/Image";
import useInbox from "hooks/useInbox.ts";
import { useUser } from "contexts/userContext/userContext";
import { useToasts } from "react-toast-notifications";
import { MailRemovedNotification } from "./MailRemovedNotification";

interface RemoveExpiredMailModalI {
  encryptedMailIds: string[];
  cb: () => void;
  approvalAction?: () => void;
  titleKey?: string;
  textKey?: string;
}

const radioOptionsEnum = {
  // option value : textKey
  trash: "remove-to-trash",
  forever: "remove-forever",
};

const RemoveExpiredMailModal: FC<RemoveExpiredMailModalI> = (props) => {
  const { getTextByKey } = useContent();
  const { setModal } = useModal();
  const { removeMailsAction, removeMailForever, fetchMailsCount } = useInbox();
  const { getUserData } = useUser();
  const { addToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] =
    useState<keyof typeof radioOptionsEnum>("trash");

  const handleApprove = async () => {
    const firstId = props.encryptedMailIds?.[0];
    if (props?.approvalAction) {
      props.approvalAction();
    } else {
      let response;

      if (selectedOption === "trash") {
        response = await removeMailsAction({
          mailsArray: [firstId],
          isUndo: false,
          setLoadingStatus: setIsLoading,
          userId: getUserData()?.UserId,
        });
      } else if (selectedOption === "forever") {
        response = await removeMailForever({
          encryptedMailIds: [firstId],
          setLoadingStatus: setIsLoading,
          userId: getUserData()?.UserId,
        });
      }

      if (response?.status === "success") {
        const toastTextKey =
          selectedOption === "trash"
            ? "remove-mail-notification"
            : "mail-removed-forever";
        if (selectedOption === "trash") {
          const undoAction = async () =>
            await removeMailsAction({
              mailsArray: [firstId],
              isUndo: true,
              setLoadingStatus: setIsLoading,
              userId: getUserData()?.UserId,
            });
          addToast(
            <MailRemovedNotification
              mailStatus={"removed"}
              activeArray={props.encryptedMailIds}
              pendingRemovedArray={null}
              undoAction={undoAction}
              showToast={undefined}
            />,
            {
              appearance: "success",
            }
          );
        } else {
          addToast(getTextByKey(toastTextKey), {
            appearance: "success",
          });
        }
        props?.cb?.();
        setTimeout(() => {
          fetchMailsCount();
        }, 1000);
        setModal({});
      }
    }
  };

  return (
    <div className="remove-completely-modal">
      <h1>{getTextByKey("remove-expired-mail-title")}</h1>
      <p>{getTextByKey("remove-expired-mail-description")}</p>
      <div className="selection-container">
        {Object.entries(radioOptionsEnum).map(([key, textKey]) => {
          return (
            <div
              className="radio-elem"
              onClick={() =>
                setSelectedOption(key as keyof typeof radioOptionsEnum)
              }
            >
              <Image
                name={
                  selectedOption === key ? "radio-checked" : "radio-unchecked"
                }
              />
              {getTextByKey(textKey)}
            </div>
          );
        })}
      </div>
      <div className="buttons">
        <Button onClick={() => setModal({})} blank className="cancel-button">
          {getTextByKey("trash-cancel-popup")}
        </Button>
        <Button onClick={handleApprove} disabled={isLoading}>
          {isLoading && (
            <Image
              name="preloader"
              ext="gif"
              width={40}
              height={40}
              className="loader-icon"
            />
          )}
          {getTextByKey("remove-approve")}
        </Button>
      </div>
    </div>
  );
};

export default RemoveExpiredMailModal;
